<!-- =========================================================================================
  File Name: FormPostDialog.vue
  Description: Create Or Update Post - Dialog component
========================================================================================== -->
<i18n src="@/locales/modules/PostTranslate.json"></i18n>

<template>
	<el-dialog
		class="custom-nested-dialog form-post-dialog"
		append-to-body
		:before-close="
			setDialogHide =>
				handleCloseDialog(() => {
					setDialogHide();
				}, isFormDirty)
		"
		:visible.sync="isDialogActiveLocal"
		:title="$t(`${value.id ? 'UpdatePost' : 'CreatePost'}`)"
	>
		<PerfectScrollbar ref="vueScrollBar" :settings="settings">
			<div class="p-6">
				<cbs-editor
					:class="[
						'form-post-dialog__content',
						{ 'is-focus': this.isEditorFocusing }
					]"
					v-model="value.content"
					@focus="() => (this.isEditorFocusing = true)"
					@blur="() => (this.isEditorFocusing = false)"
				/>

				<div class="form-post-dialog__attachs mt-6">
					<cbs-upload-ref
						multiple
						v-model="value.files"
					/>
				</div>

				<div class="form-post-dialog__tags mt-6 flex items-center">
					<legend class="whitespace-no-wrap w-32">
						{{ $t("Tags") }}:
					</legend>
					<v-select
						multiple
						label="name"
						v-model="value.tags"
						:options="postTags"
						:reduce="tag => tag.id"
						:close-on-select="false"
						class="select-multiple w-full"
						:placeholder="`${$t('Actions.Choose')} ${$t('Tag').toLowerCase()}`"
					>
						<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
					</v-select>
				</div>

				<div
					class="form-post-dialog__categories mt-6 flex items-center"
				>
					<legend class="whitespace-no-wrap w-32">
						{{ $t("Category") }}<span class="text-danger">&nbsp;*</span>:
					</legend>
					<v-select
						label="title"
						v-model="value.categoryId"
						:options="postCategories"
						:reduce="category => category.id"
						class="w-full"
						:placeholder="`${$t('Actions.Choose')} ${$t('Category').toLowerCase()}`"
					>
						<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
					</v-select>
				</div>
			</div>
		</PerfectScrollbar>

		<div class="flex justify-center" slot="footer">
			<vs-button
				class="w-2/3 sm:w-1/3 font-bold"
				:disabled="isDisabledSubmit"
				@click.stop="$emit('onSubmit')"
				>{{ `${this.value.id ? $t('Actions.Save') : this.$t('SubmitPost')}` }}</vs-button
			>
		</div>
	</el-dialog>
</template>

<script>
import _ from "lodash";
import vSelect from "vue-select";
import TagGQL from "@/http/requests/inside/tag_gql";
import CategoryGQL from "@/http/requests/inside/category_gql";

import CbsEditor from "@/components/cbs-editor/CbsEditor";
import CbsUploadRef from "@/components/cbs-upload-ref/CbsUploadRef";

export default {
	components: { vSelect, CbsEditor, CbsUploadRef },
	props: {
		isDialogActive: {
			type: Boolean,
			required: true
		},
		value: {
			type: Object,
			required: true,
			validator: prop =>
				_.has(prop, "id") &&
				_.has(prop, "tags") &&
				_.has(prop, "files") &&
				_.has(prop, "content") &&
				_.has(prop, "categoryId")
		}
	},
	emits: {
		onSubmit: {}
	},
	data() {
		return {
			postTags: [],
			postCategories: [],

			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			},
			initFormData: {},
			isFormDirty: false,
			isDisabledSubmit: true,
			isEditorFocusing: false
		};
	},
	computed: {
		isDialogActiveLocal: {
			get() {
				return this.isDialogActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closeDialog");
				}
			}
		},
		isDataChanged() {
			return (
				JSON.stringify(this.value) !== JSON.stringify(this.initFormData)
			);
		}
	},
	watch: {
		isDialogActive(val) {
			if (!val) return;
		},
		value: {
			handler: function() {
				this.isFormDirty = this.isDataChanged;
				this.isDisabledSubmit = !this.isDataChanged || !this.value.content || !this.value.categoryId;
			},
			deep: true
		}
	},
	async created() {
		const [tagDataResponse, categoryDataResponse] = await Promise.all([
			TagGQL.getTags(),
			CategoryGQL.getCategories()
		]);
		this.postTags = tagDataResponse.tags || [];
		this.postCategories = categoryDataResponse.categories || [];
		this.initFormData = Object.assign({}, this.value);
	}
};
</script>

<style lang="scss" scoped>
.form-post-dialog {
	::v-deep .el-dialog {
		width: 850px;
	}

	.form-post-dialog__content {
		::v-deep .ql-editor {
			color: var(--color-black);
			height: 40vh;
			max-height: 350px;

			&::before {
				left: 15px;
				font-style: normal;
			}
		}

		&.is-focus {
			::v-deep .ql-editor {
				&::before {
					transform: translateX(5px);
					transition: all 0.2s ease;
				}
			}
		}
	}

	.form-post-dialog__attachs {
		::v-deep .file-item,
		::v-deep .cbs-upload .upload-trigger {
			width: 90px;
			height: 90px;
		}
	}
}
</style>
